import { supabase } from '../../lib/supabase'
import { periodNumberFor } from '../../lib/payPeriods'
import { todayIso } from '../reports/reportDates'

// A published version of the agent's team schedule the agent has not yet
// acknowledged.
export type PendingAcknowledgement = {
  teamId: string
  periodStart: string
  periodEnd: string
  version: number
  // A version after the first is an amendment.
  amended: boolean
  periodLabel: string
}

export type AcknowledgeResult = { ok: true } | { ok: false; message: string }

const FAILED = 'The acknowledgement could not be saved. Try again.'

async function getMyEmployeeId(userId: string): Promise<string | null> {
  const { data } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  return data?.id ?? null
}

type VersionRow = {
  team_id: string
  period_start: string
  period_end: string
  version: number
  entries: { employeeId?: string }[]
}

/**
 * The latest published version of each of the agent's team's pay periods that
 * has not ended, which names the agent and which they have not acknowledged. An
 * amendment is a new version, so it comes up again even when the version before
 * was acknowledged. Row-level security limits the versions to the agent's own
 * team.
 */
export async function getPendingAcknowledgements(userId: string): Promise<PendingAcknowledgement[]> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return []

  const [versions, acknowledged] = await Promise.all([
    supabase.from('schedule_versions').select('team_id, period_start, period_end, version, entries').gte('period_end', todayIso()).order('version', { ascending: false }),
    supabase.from('schedule_acknowledgements').select('team_id, period_start, version').eq('employee_id', employeeId),
  ])
  if (versions.error || !versions.data) return []

  const done = new Set(((acknowledged.data ?? []) as { team_id: string; period_start: string; version: number }[]).map((row) => `${row.team_id}|${row.period_start}|${row.version}`))
  const latest = new Map<string, VersionRow>()
  for (const row of versions.data as unknown as VersionRow[]) {
    const key = `${row.team_id}|${row.period_start}`
    if (!latest.has(key)) latest.set(key, row)
  }

  return Array.from(latest.values())
    .filter((row) => row.entries.some((entry) => entry.employeeId === employeeId) && !done.has(`${row.team_id}|${row.period_start}|${row.version}`))
    .sort((a, b) => a.period_start.localeCompare(b.period_start))
    .map((row) => ({
      teamId: row.team_id,
      periodStart: row.period_start,
      periodEnd: row.period_end,
      version: row.version,
      amended: row.version > 1,
      periodLabel: `Pay Period ${periodNumberFor(row.period_start)}`,
    }))
}

// Records that the agent has seen this version. Row-level security holds the
// rules: it is their own, of their own team's schedule, once.
export async function acknowledgeSchedule(userId: string, item: PendingAcknowledgement): Promise<AcknowledgeResult> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return { ok: false, message: FAILED }
  const { error } = await supabase.from('schedule_acknowledgements').insert({
    team_id: item.teamId,
    period_start: item.periodStart,
    version: item.version,
    employee_id: employeeId,
    created_by: userId,
  })
  // 23505: already acknowledged, as from another tab, which is what was asked for.
  return error && error.code !== '23505' ? { ok: false, message: FAILED } : { ok: true }
}
